<template>
  <ModalStep
    id="impersonation-reason-modal-content"
    :header="{
      title: t('impersonation.modal.title'),
    }"
    :onExitHandler="onExitHandler"
  >
    <template #bodyInformation>
      <strong data-test-id="impersonation-modal-email">
        {{ t('impersonation.modal.message', { email: targetUserEmail }) }}
      </strong>
    </template>

    <template #bodyContent>
      <form @submit.prevent="handleConfirm" class="impersonation-reason-form">
        <div class="form-field">
          <label for="impersonation-reason">{{ t('impersonation.modal.reasonLabel') }}</label>
          <select
            id="impersonation-reason"
            v-model="selectedPresetReason"
            @change="handlePresetChange"
            class="form-select"
            data-test-id="select-impersonation-reason"
          >
            <option value="">{{ t('impersonation.modal.selectReason') }}</option>
            <option v-for="reason in presetReasons" :key="reason.value" :value="reason.value">
              {{ t(reason.label) }}
            </option>
            <option value="custom">{{ t('impersonation.modal.customReason') }}</option>
          </select>
        </div>

        <div v-if="showCustomInput" class="form-field">
          <label for="impersonation-custom-reason">{{ t('impersonation.modal.customReasonLabel') }}</label>
          <textarea
            id="impersonation-custom-reason"
            v-model="customReason"
            :placeholder="t('impersonation.modal.customReasonPlaceholder')"
            class="form-textarea"
            data-test-id="textarea-impersonation-custom-reason"
            rows="3"
            maxlength="1000"
          ></textarea>
          <span class="char-count">{{ customReason.length }}/1000</span>
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>
      </form>
    </template>

    <template #footer>
      <div class="c-btns-container">
        <CustomButton
          usage="button"
          type="button"
          color="white"
          @emit:click="onExitHandler"
          :text="t('impersonation.modal.cancel')"
        />
        <CustomButton
          usage="button"
          type="button"
          color="primary"
          @emit:click="handleConfirm"
          :isDisabled="!isValid || isSubmitting"
          :isLoading="isSubmitting"
          data-test-id="impersonation-confirm-button"
          :text="t('impersonation.modal.confirm')"
        />
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from '@/lib/i18n'
import { useImpersonation } from '@/features/admin/users/hooks/use-impersonation.hook'
import modalsStore from '@/features/ui/store/modal/modal.store'
import type { ImpersonationReasonModalType } from '@/features/ui/store/modal/modal.store'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import CustomButton from '@/features/ui/components/button/custom-button.vue'

const { t } = useI18n()
const { startImpersonation, isStarting } = useImpersonation()

defineProps<{
  onExitHandler: () => void
}>()

const modalData = computed(() => modalsStore.modals['impersonation-reason'] as ImpersonationReasonModalType)
const targetUserEmail = computed(() => modalData.value.attachedData.targetUserEmail || '')
const targetUserId = computed(() => modalData.value.attachedData.targetUserId || '')
const targetUserRoles = computed(() => modalData.value.attachedData.targetUserRoles || [])

const presetReasons = [
  { value: 'technical_support', label: 'impersonation.reasons.technicalSupport' },
  { value: 'bug_investigation', label: 'impersonation.reasons.bugInvestigation' },
  { value: 'user_assistance', label: 'impersonation.reasons.userAssistance' },
  { value: 'data_verification', label: 'impersonation.reasons.dataVerification' },
  { value: 'account_issue', label: 'impersonation.reasons.accountIssue' },
]

const selectedPresetReason = ref('')
const customReason = ref('')
const showCustomInput = ref(false)
const isSubmitting = computed(() => isStarting.value)
const error = ref('')

const isValid = computed(() => {
  if (selectedPresetReason.value === 'custom') {
    return customReason.value.trim().length > 0
  }
  return selectedPresetReason.value !== ''
})

function handlePresetChange() {
  showCustomInput.value = selectedPresetReason.value === 'custom'
  error.value = ''
}

function handleConfirm() {
  if (!isValid.value) {
    error.value = t('impersonation.modal.errorRequired')
    return
  }

  const reason =
    selectedPresetReason.value === 'custom'
      ? customReason.value.trim()
      : t(presetReasons.find((r) => r.value === selectedPresetReason.value)?.label || '')

  if (targetUserId.value) {
    startImpersonation({
      targetUserId: targetUserId.value,
      reason,
      targetUserRoles: targetUserRoles.value,
    })
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/utils';

.impersonation-reason-form {
  display: flex;
  flex-direction: column;
  gap: $s * 2;

  .form-field {
    display: flex;
    flex-direction: column;
    gap: $s;

    label {
      font-weight: 600;
      font-size: 14px;
      color: $black;
    }
  }

  .form-select,
  .form-textarea {
    width: 100%;
    padding: $s * 1.5;
    border: 1px solid rgba($black, 0.2);
    border-radius: $s / 2;
    font-size: 14px;
    font-family: inherit;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: var(--color-primary);
    }
  }

  .form-textarea {
    resize: vertical;
    min-height: 80px;
  }

  .char-count {
    font-size: 12px;
    color: rgb($text-muted);
    text-align: right;
  }

  .error-message {
    padding: $s * 1.5;
    background-color: #fee;
    border: 1px solid #fcc;
    border-radius: $s / 2;
    color: #c00;
    font-size: 14px;
  }
}
</style>
